import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DatePicker, Button, Spin, message, Table, Tooltip, Select } from 'antd';
import { DownloadOutlined, ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { fetchActivitiesByLocation, fetchPlanVersions } from './api';

const { RangePicker } = DatePicker;

// CSS Styles matching the dashboard design
const styles = {
  container: {
    padding: '24px',
    fontFamily: "'DM Sans', -apple-system, sans-serif",
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  header: {
    background: '#ffffff',
    borderBottom: '1px solid #e2e8f0',
    padding: '16px 20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1a202c',
    letterSpacing: '-0.5px',
    margin: 0,
  },
  headerSubtitle: {
    fontSize: '13px',
    color: '#718096',
    marginTop: '4px',
  },
  headerMeta: {
    display: 'flex',
    gap: '24px',
    alignItems: 'center',
    fontSize: '14px',
    color: '#4a5568',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    background: '#ebf8ff',
    border: '1px solid #90cdf4',
    borderRadius: '6px',
    fontWeight: '500',
    fontSize: '13px',
    color: '#2c5282',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    background: '#3182ce',
    borderRadius: '50%',
    animation: 'pulse 2s infinite',
  },
  filters: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '12px 16px',
    // boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    // marginBottom: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    // border: '1px solid #e2e8f0',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  timeSlicers: {
    display: 'flex',
    gap: '4px',
    padding: '2px',
    background: '#f7fafc',
    borderRadius: '6px',
    border: '1px solid #e2e8f0',
  },
  timeBtn: {
    padding: '4px 10px',
    border: 'none',
    background: 'transparent',
    color: '#718096',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer',
    borderRadius: '4px',
    transition: 'all 0.2s ease',
  },
  timeBtnActive: {
    background: '#2c5282',
    color: 'white',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  btnExport: {
    marginLeft: 'auto',
    padding: '8px 16px',
    background: '#2c5282',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
    height: '36px',
  },
  kpiGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  kpiCard: {
    background: '#ffffff',
    borderRadius: '8px',
    padding: '14px 16px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0',
    position: 'relative',
    overflow: 'hidden',
  },
  kpiCardAccent: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
  },
  kpiHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  kpiTitle: {
    fontSize: '11px',
    fontWeight: '600',
    color: '#718096',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  kpiIcon: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
  },
  kpiValue: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    lineHeight: 1,
    marginBottom: '6px',
    fontFamily: "'JetBrains Mono', monospace",
  },
  kpiSubtitle: {
    fontSize: '12px',
    color: '#4a5568',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  progressBar: {
    height: '4px',
    background: '#f7fafc',
    borderRadius: '2px',
    overflow: 'hidden',
    marginTop: '8px',
  },
  progressFill: {
    height: '100%',
    borderRadius: '2px',
    transition: 'width 0.6s ease',
  },
  tableCard: {
    background: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },
  tableHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tableTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#1a202c',
    margin: 0,
  },
  tableActions: {
    display: 'flex',
    gap: '12px',
  },
  btnSecondary: {
    padding: '8px 16px',
    background: '#f7fafc',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    color: '#4a5568',
    height: '36px',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    fontFamily: "'JetBrains Mono', monospace",
  },
  badgeSuccess: {
    background: '#c6f6d5',
    color: '#22543d',
  },
  badgeWarning: {
    background: '#fef5e7',
    color: '#7d6608',
  },
  badgeDanger: {
    background: '#fed7d7',
    color: '#742a2a',
  },
  badgeInfo: {
    background: '#e9d8fd',
    color: '#553c9a',
  },
  insights: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '12px',
    padding: '28px',
    marginTop: '16px',
    color: 'white',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.08)',
  },
  insightsTitle: {
    fontSize: '18px',
    fontWeight: '700',
    marginBottom: '16px',
    opacity: 0.95,
  },
  insightsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
  },
  insightItem: {
    background: 'rgba(255, 255, 255, 0.15)',
    padding: '16px',
    borderRadius: '8px',
    backdropFilter: 'blur(10px)',
  },
  insightValue: {
    display: 'block',
    fontSize: '24px',
    marginBottom: '4px',
    fontFamily: "'JetBrains Mono', monospace",
    fontWeight: '700',
  },
  insightLabel: {
    fontSize: '13px',
    opacity: 0.9,
  },
};

const PlanCycleVariance = () => {
  // Calculate default date/time range dynamically
  const calculateDefaultRange = () => {
    const now = new Date();
    const rounded = new Date(now);
    if (rounded.getMinutes() > 0 || rounded.getSeconds() > 0 || rounded.getMilliseconds() > 0) {
      rounded.setHours(rounded.getHours() + 1);
    }
    rounded.setMinutes(0, 0, 0);
    const end = dayjs(rounded);
    const start = end.subtract(23, 'hour');
    return [start, end];
  };

  const [dateTimeRange, setDateTimeRange] = useState(() => calculateDefaultRange());
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [activeTimeRange, setActiveTimeRange] = useState(null); // Quick time range selector
  
  // Plan version state
  const [planVersions, setPlanVersions] = useState([]);
  const [selectedPlanVersion, setSelectedPlanVersion] = useState(null); // null means latest
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Handle quick time range selection
  const handleTimeRangeChange = (range) => {
    setActiveTimeRange(range);
    const now = dayjs();
    let start;
    switch (range) {
      case '1D':
        start = now.subtract(1, 'day');
        break;
      case '1W':
        start = now.subtract(1, 'week');
        break;
      case '1M':
        start = now.subtract(1, 'month');
        break;
      case '3M':
        start = now.subtract(3, 'month');
        break;
      case '6M':
        start = now.subtract(6, 'month');
        break;
      default:
        return;
    }
    setDateTimeRange([start, now]);
  };

  // Format datetime string for display
  const formatDateTime = (dateStr) => {
    if (!dateStr || dateStr === 'running') return dateStr || '-';
    const date = dayjs(dateStr);
    return date.isValid() ? date.format('DD-MM-YYYY HH:mm') : '-';
  };

  // Calculate duration between two dates
  const calculateDuration = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return '-';
    if (endDateStr === 'running') return 'running';
    const start = dayjs(startDateStr);
    const end = dayjs(endDateStr);
    if (!start.isValid() || !end.isValid()) return '-';
    const diffMs = end.diff(start);
    if (diffMs < 0) return '-';
    const totalMinutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:00`;
  };

  // Get equipment category from name
  const getAreaFromEquipmentName = (equipmentName) => {
    if (!equipmentName) return 'Other';
    const name = String(equipmentName).toUpperCase();
    if (name.startsWith('COMP-') || name.startsWith('COMP')) return 'Compression';
    if (name.startsWith('COAT-') || name.startsWith('COAT')) return 'Coating';
    if (name.startsWith('CAPS-') || name.startsWith('CAPS')) return 'Capsulation';
    if (name.startsWith('IMP-') || name.startsWith('IMP')) return 'Imprinting';
    if (name.startsWith('FBP-') || name.startsWith('FBP')) return 'FBP';
    if (/^P\d+\s*(PRIMARY|SECONDARY)$/.test(name)) return 'Packing';
    if (name.match(/^B\d/)) return 'Blending';
    if (name.match(/^G\d/)) return 'Granulation';
    return 'Other';
  };

  // Check if activity is production
  const isProductionActivity = (activity) => {
    const activityType = (activity.activity_type || '').toLowerCase();
    if (activityType === 'cleaning' || activityType === 'maintenance') return false;
    const hasBatchNumber = activity.plan_product || activity.actual_product;
    const isProduction = activityType === 'manufacturing' || 
                        activityType === 'operation' ||
                        activityType === 'production' ||
                        (activity.plan_activity || '').toUpperCase() === 'PRODUCTION';
    return hasBatchNumber || isProduction;
  };

  // Calculate plan variation (Actual Start - Planned Start)
  const calculatePlanVariation = (planStart, actualStart) => {
    if (!planStart || !actualStart) return { value: '-', ms: null };
    const planStartDate = dayjs(planStart);
    const actualStartDate = dayjs(actualStart);
    if (!planStartDate.isValid() || !actualStartDate.isValid()) return { value: '-', ms: null };
    const varianceMs = actualStartDate.diff(planStartDate);
    const isNegative = varianceMs < 0;
    const absVarianceMs = Math.abs(varianceMs);
    const totalMinutes = Math.floor(absVarianceMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}:${minutes.toString().padStart(2, '0')}:00`;
    return { value: isNegative ? `-${formatted}` : formatted, ms: varianceMs };
  };

  // Calculate batch cycle variation (Actual Duration - Planned Duration)
  const calculateBatchCycleVariation = (planStart, planEnd, actualStart, actualEnd) => {
    if (!planStart || !planEnd || !actualStart || !actualEnd) return { value: '-', ms: null };
    if (actualEnd === 'running') return { value: 'running', ms: null };
    const planStartDate = dayjs(planStart);
    const planEndDate = dayjs(planEnd);
    const actualStartDate = dayjs(actualStart);
    const actualEndDate = dayjs(actualEnd);
    if (!planStartDate.isValid() || !planEndDate.isValid() || 
        !actualStartDate.isValid() || !actualEndDate.isValid()) return { value: '-', ms: null };
    const plannedMs = planEndDate.diff(planStartDate);
    const actualMs = actualEndDate.diff(actualStartDate);
    if (plannedMs < 0 || actualMs < 0) return { value: '-', ms: null };
    const varianceMs = actualMs - plannedMs;
    const isNegative = varianceMs < 0;
    const absVarianceMs = Math.abs(varianceMs);
    const totalMinutes = Math.floor(absVarianceMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${hours}:${minutes.toString().padStart(2, '0')}:00`;
    return { value: isNegative ? `-${formatted}` : formatted, ms: varianceMs };
  };

  // Process API data into table rows
  const processData = useCallback((apiData) => {
    const rows = [];
    let rowKey = 0;
    if (!apiData?.data) return rows;

    Object.entries(apiData.data).forEach(([equipmentName, equipmentData]) => {
      const area = getAreaFromEquipmentName(equipmentName);
      const eqpSequence = equipmentData.eqp_sequence ?? 9999;
      const allActivities = [
        ...(equipmentData.matched || []),
        ...(equipmentData.not_started || []),
        ...(equipmentData.unplanned || [])
      ];
      const productionActivities = allActivities.filter(isProductionActivity);

      productionActivities.forEach((activity) => {
        const batchNumber = activity.plan_product || activity.actual_product || '-';
        const batchesMatch = activity.plan_product && activity.actual_product && 
                           activity.plan_product.toLowerCase() === activity.actual_product.toLowerCase();
        
        const planVariationResult = batchesMatch 
          ? calculatePlanVariation(activity.plan_start_date, activity.actual_start_time)
          : { value: '-', ms: null };
        
        const batchCycleResult = batchesMatch 
          ? calculateBatchCycleVariation(
              activity.plan_start_date, activity.plan_end_date, 
              activity.actual_start_time, activity.actual_end_time
            )
          : { value: '-', ms: null };
        
        rows.push({
          key: rowKey++,
          area,
          equipmentName,
          eqpSequence: activity.eqp_sequence ?? eqpSequence,
          batchNumber,
          plannedStart: formatDateTime(activity.plan_start_date),
          plannedEnd: formatDateTime(activity.plan_end_date),
          planDuration: calculateDuration(activity.plan_start_date, activity.plan_end_date),
          actualStart: formatDateTime(activity.actual_start_time),
          actualEnd: formatDateTime(activity.actual_end_time),
          actualDuration: calculateDuration(activity.actual_start_time, activity.actual_end_time),
          planVariation: planVariationResult.value,
          planVarianceMs: planVariationResult.ms,
          batchCycleVariation: batchCycleResult.value,
          batchCycleMs: batchCycleResult.ms,
          status: activity.status,
          batchesMatch,
          rawPlanStart: activity.plan_start_date,
          rawActualStart: activity.actual_start_time,
          rawActualEnd: activity.actual_end_time
        });
      });
    });

    rows.sort((a, b) => {
      if (a.eqpSequence !== b.eqpSequence) return a.eqpSequence - b.eqpSequence;
      if (a.rawPlanStart && b.rawPlanStart) return new Date(a.rawPlanStart) - new Date(b.rawPlanStart);
      return 0;
    });
    return rows;
  }, []);

  // Calculate KPI statistics
  const kpiStats = useMemo(() => {
    // TILE 1: Total Batches Planned - count all records
    const totalPlanned = tableData.length;
    
    // TILE 2: Total Batches Manufactured - where Actual Start is NOT null/empty/"-"
    const manufacturedBatches = tableData.filter(r => r.actualStart && r.actualStart !== '-');
    const totalManufactured = manufacturedBatches.length;
    
    // TILE 3: Production Status
    // Completed: Actual End has valid date (NOT null/empty/"-"/"running")
    const completedBatches = manufacturedBatches.filter(r => 
      r.actualEnd && r.actualEnd !== '-' && r.actualEnd !== 'running' && r.rawActualEnd !== 'running'
    );
    const completed = completedBatches.length;
    // Running: Actual End = "running"
    const running = manufacturedBatches.filter(r => 
      r.actualEnd === 'running' || r.rawActualEnd === 'running'
    ).length;
    
    // TILE 4: On-Time Start Performance
    // Numerator: Plan Variation <= 0 (includes both completed AND running)
    // Denominator: Total Manufactured
    const onTimeStartBatches = manufacturedBatches.filter(r => 
      r.planVarianceMs !== null && r.planVarianceMs <= 0
    );
    const onTimeStart = onTimeStartBatches.length;
    const onTimeStartPct = totalManufactured > 0 
      ? ((onTimeStart / totalManufactured) * 100).toFixed(1) 
      : '0.0';
    
    // TILE 5: On-Time Completion Performance
    // Numerator: Batch Cycle Variation <= 0
    // Denominator: Completed batches ONLY (exclude running)
    const onTimeCompletionBatches = completedBatches.filter(r => 
      r.batchCycleMs !== null && r.batchCycleMs <= 0
    );
    const onTimeCompletion = onTimeCompletionBatches.length;
    const onTimeCompletionPct = completed > 0 
      ? ((onTimeCompletion / completed) * 100).toFixed(1) 
      : '0.0';

    return { 
      totalPlanned,
      totalManufactured, 
      completed,
      running,
      onTimeStart, 
      onTimeStartPct,
      onTimeCompletion,
      onTimeCompletionPct
    };
  }, [tableData]);

  // Fetch data from API
  const fetchData = useCallback(async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      
      // Build API params - only include plan_version if a specific version is selected
      const apiParams = {
        start_date: startDate,
        end_date: endDate
      };
      if (selectedPlanVersion !== null) {
        apiParams.plan_version = selectedPlanVersion;
      }
      
      const response = await fetchActivitiesByLocation(apiParams);
      if (response?.success) {
        const processed = processData(response);
        setTableData(processed);
      } else {
        message.error('Failed to fetch data');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      message.error('Error fetching data: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, [dateTimeRange, selectedPlanVersion, processData]);

  // Export to Excel
  const handleExportExcel = async () => {
    if (!dateTimeRange || dateTimeRange.length !== 2) {
      message.warning('Please select a date range');
      return;
    }
    setExporting(true);
    try {
      const [start, end] = dateTimeRange;
      const startDate = start.format('YYYY-MM-DD HH:mm:ss');
      const endDate = end.format('YYYY-MM-DD HH:mm:ss');
      const headers = ['Area', 'Batch Number', 'Planned Start', 'Planned End', 'Plan Duration', 
                       'Actual Start', 'Actual End', 'Actual Duration', 'Plan Variation', 'Batch Cycle Variation'];
      const csvRows = [
        headers.join(','),
        ...tableData.map(row => [
          `${row.area} - ${row.equipmentName}`,
          row.batchNumber, row.plannedStart, row.plannedEnd, row.planDuration,
          row.actualStart, row.actualEnd, row.actualDuration, row.planVariation, row.batchCycleVariation
        ].map(cell => `"${cell}"`).join(','))
      ];
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `plan_cycle_variance_${startDate.split(' ')[0]}_to_${endDate.split(' ')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('Export completed successfully');
    } catch (error) {
      console.error('Error exporting:', error);
      message.error('Error exporting data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (dateTimeRange && dateTimeRange.length === 2) {
      fetchData();
    }
  }, [dateTimeRange, selectedPlanVersion]);

  // Fetch plan versions on mount
  useEffect(() => {
    const loadPlanVersions = async () => {
      setLoadingVersions(true);
      try {
        const response = await fetchPlanVersions();
        if (response?.success && response.data) {
          setPlanVersions(response.data);
        }
      } catch (error) {
        console.error('Error loading plan versions:', error);
      } finally {
        setLoadingVersions(false);
      }
    };
    loadPlanVersions();
  }, []);

  // Table columns
  const columns = [
    {
      title: 'Area',
      dataIndex: 'area',
      key: 'area',
      width: 160,
      fixed: 'left',
      filters: [
        { text: 'Granulation', value: 'Granulation' },
        { text: 'FBP', value: 'FBP' },
        { text: 'Blending', value: 'Blending' },
        { text: 'Compression', value: 'Compression' },
        { text: 'Coating', value: 'Coating' },
        { text: 'Capsulation', value: 'Capsulation' },
        { text: 'Imprinting', value: 'Imprinting' },
        { text: 'Packing', value: 'Packing' },
        { text: 'Other', value: 'Other' }
      ],
      onFilter: (value, record) => record.area === value,
      sorter: (a, b) => a.eqpSequence - b.eqpSequence,
      defaultSortOrder: 'ascend',
      render: (area, record) => (
        <span style={{ fontWeight: '600' }}>{area} - {record.equipmentName}</span>
      )
    },
    {
      title: 'Batch Number',
      dataIndex: 'batchNumber',
      key: 'batchNumber',
      width: 150,
      render: (value) => <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{value}</span>
    },
    {
      title: 'Planned Start',
      dataIndex: 'plannedStart',
      key: 'plannedStart',
      width: 150,
      sorter: (a, b) => {
        if (!a.rawPlanStart) return 1;
        if (!b.rawPlanStart) return -1;
        return new Date(a.rawPlanStart) - new Date(b.rawPlanStart);
      }
    },
    { title: 'Planned End', dataIndex: 'plannedEnd', key: 'plannedEnd', width: 150 },
    { title: 'Plan Duration', dataIndex: 'planDuration', key: 'planDuration', width: 120, align: 'center' },
    {
      title: 'Actual Start',
      dataIndex: 'actualStart',
      key: 'actualStart',
      width: 150,
      sorter: (a, b) => {
        if (!a.rawActualStart) return 1;
        if (!b.rawActualStart) return -1;
        return new Date(a.rawActualStart) - new Date(b.rawActualStart);
      }
    },
    { title: 'Actual End', dataIndex: 'actualEnd', key: 'actualEnd', width: 150 },
    { title: 'Actual Duration', dataIndex: 'actualDuration', key: 'actualDuration', width: 130, align: 'center' },
    {
      title: 'Plan Variation',
      dataIndex: 'planVariation',
      key: 'planVariation',
      width: 130,
      align: 'center',
      render: (value) => {
        if (value === '-' || value === 'running') return value;
        const isNegative = value.startsWith('-');
        return (
          <span style={{
            ...styles.badge,
            ...(isNegative ? styles.badgeSuccess : styles.badgeDanger)
          }}>
            {value}
          </span>
        );
      }
    },
    {
      title: 'Batch Cycle Variation',
      dataIndex: 'batchCycleVariation',
      key: 'batchCycleVariation',
      width: 140,
      align: 'center',
      render: (value) => {
        if (value === '-' || value === 'running') return value;
        const isNegative = value.startsWith('-');
        return (
          <span style={{
            ...styles.badge,
            ...(isNegative ? styles.badgeSuccess : styles.badgeDanger)
          }}>
            {value}
          </span>
        );
      }
    }
  ];

  const dateRangeDisplay = dateTimeRange && dateTimeRange.length === 2
    ? `${dateTimeRange[0].format('DD MMM')} - ${dateTimeRange[1].format('DD MMM YYYY')}`
    : 'Select Date Range';

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={{display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'center'}}>
            <div>
          <h1 style={styles.headerTitle}>Plan and Cycle Time Variance</h1>
          <div style={styles.headerSubtitle}>
            Real-time monitoring of planned vs actual production schedules
          </div>
          </div>
           {/* Filters */}
      <div style={styles.filters}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Date Range</label>
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            value={dateTimeRange}
            onChange={(dates) => {
              setDateTimeRange(dates);
              setActiveTimeRange(null);
            }}
            style={{ minWidth: '340px' }}
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Quick Select</label>
          <div style={styles.timeSlicers}>
            {['1D', '1W', '1M', '3M', '6M'].map((range) => (
              <button
                key={range}
                style={{
                  ...styles.timeBtn,
                  ...(activeTimeRange === range ? styles.timeBtnActive : {})
                }}
                onClick={() => handleTimeRangeChange(range)}
              >
                {range}
              </button>
            ))}
          </div>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Plan Version</label>
          <Select
            value={selectedPlanVersion}
            onChange={(value) => setSelectedPlanVersion(value)}
            loading={loadingVersions}
            style={{ minWidth: '180px' }}
            placeholder="Select version"
          >
            <Select.Option value={null}>Latest Version</Select.Option>
            {planVersions.map((v) => (
              <Select.Option key={v.plan_version} value={v.plan_version}>
                v{v.plan_version} - {dayjs(v.published_at).format('DD MMM HH:mm')}
              </Select.Option>
            ))}
          </Select>
        </div>
        {/* <Button
          icon={<ReloadOutlined />}
          onClick={fetchData}
          loading={loading}
          style={styles.btnSecondary}
        >
          Refresh
        </Button> */}
      </div>
        </div>
        
        <div style={styles.headerMeta}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: '600', color: '#1a202c' }}>{dateRangeDisplay}</div>
            <div style={{ fontSize: '12px' }}>Analysis Period</div>
          </div>
        </div>
      </div>

     

      {/* KPI Cards */}
      <div style={styles.kpiGrid}>
        {/* TILE 1: Total Batches Planned */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: 'linear-gradient(90deg, #3182ce, #2c5282)' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Total Batches Planned</div>
            <Tooltip title="Total number of batches in the production schedule">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.totalPlanned}</div>
          <div style={styles.kpiSubtitle}>Batches in production schedule</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: '100%', background: 'linear-gradient(90deg, #3182ce, #2c5282)' }}></div>
          </div>
        </div>

        {/* TILE 2: Total Batches Manufactured */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: '#38a169' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Total Manufactured</div>
            <Tooltip title="Formula: COUNT(records where Actual Start has a value)">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.totalManufactured}</div>
          <div style={styles.kpiSubtitle}>Batches with actual start time</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: kpiStats.totalPlanned > 0 ? `${(kpiStats.totalManufactured / kpiStats.totalPlanned * 100)}%` : '0%', background: '#38a169' }}></div>
          </div>
        </div>

        {/* TILE 3: Production Status */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: '#6366f1' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>Production Status</div>
            <Tooltip title="Completed: Batches with valid Actual End date | Running: Batches marked as 'running'">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>
            <span style={{ color: '#38a169' }}>{kpiStats.completed}</span>
            <span style={{ color: '#718096', fontSize: '16px', margin: '0 8px' }}>|</span>
            <span style={{ color: '#f59e0b' }}>{kpiStats.running}</span>
          </div>
          <div style={styles.kpiSubtitle}>
            <span style={{ color: '#38a169' }}>Completed</span>
            <span style={{ margin: '0 8px' }}>|</span>
            <span style={{ color: '#f59e0b' }}>Running</span>
          </div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: kpiStats.totalManufactured > 0 ? `${(kpiStats.completed / kpiStats.totalManufactured * 100)}%` : '0%', background: '#38a169' }}></div>
          </div>
        </div>

        {/* TILE 4: On-Time Start % */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: parseFloat(kpiStats.onTimeStartPct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimeStartPct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>On-Time Start %</div>
            <Tooltip title="Formula: (Batches with Plan Variation ≤ 0 / Total Manufactured) × 100. Plan Variation ≤ 0 means started on-time or early. Includes all manufactured batches.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.onTimeStartPct}%</div>
          <div style={styles.kpiSubtitle}>({kpiStats.onTimeStart}/{kpiStats.totalManufactured}) batches started on time</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${kpiStats.onTimeStartPct}%`, background: parseFloat(kpiStats.onTimeStartPct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimeStartPct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          </div>
        </div>

        {/* TILE 5: On-Time Completion % */}
        <div style={styles.kpiCard}>
          <div style={{ ...styles.kpiCardAccent, background: parseFloat(kpiStats.onTimeCompletionPct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimeCompletionPct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          <div style={styles.kpiHeader}>
            <div style={styles.kpiTitle}>On-Time Completion %</div>
            <Tooltip title="Formula: (Batches with Batch Cycle Variation ≤ 0 / Completed Batches) × 100. Batch Cycle Variation ≤ 0 means completed within or faster than planned duration. Calculated only for completed batches.">
              <InfoCircleOutlined style={{ color: '#718096', cursor: 'pointer', fontSize: '14px' }} />
            </Tooltip>
          </div>
          <div style={styles.kpiValue}>{kpiStats.onTimeCompletionPct}%</div>
          <div style={styles.kpiSubtitle}>({kpiStats.onTimeCompletion}/{kpiStats.completed}) completed on time</div>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${kpiStats.onTimeCompletionPct}%`, background: parseFloat(kpiStats.onTimeCompletionPct) >= 80 ? '#38a169' : parseFloat(kpiStats.onTimeCompletionPct) >= 50 ? '#f59e0b' : '#e53e3e' }}></div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={styles.tableCard} >
        <div style={styles.tableHeader}>
          <h2 style={styles.tableTitle}>Batch-wise Plan vs Actual Details</h2>
          <div style={styles.tableActions}>
            <Button style={styles.btnSecondary} icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
              🔄 Refresh
            </Button>
            <Button style={styles.btnSecondary} icon={<DownloadOutlined />} onClick={handleExportExcel} loading={exporting}>
              📊 Export to Excel
            </Button>
          </div>
        </div>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={tableData}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
            }}
            scroll={{ x: 1500, y: 500 }}
            sticky={{ offsetHeader: 0 }}
            size="middle"
            locale={{ emptyText: 'No production activities found for the selected date range' }}
            style={{ fontSize: '13px' }}
          />
        </Spin>
      </div>

      {/* Add keyframes for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=JetBrains+Mono:wght@400;600&display=swap');
        
        .ant-table-thead > tr > th {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
          background: #fafafa !important;
        }
        .ant-table-header {
          position: sticky !important;
          top: 0 !important;
          z-index: 10 !important;
        }
      `}</style>
    </div>
  );
};

export default PlanCycleVariance;
